/** 5.5e (2024) reference tables. SRD 5.2 content, CC-BY-4.0. */

export const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'] as const
export type Ability = (typeof ABILITIES)[number]

export const ABILITY_NAMES: Record<Ability, string> = {
  str: 'Strength',
  dex: 'Dexterity',
  con: 'Constitution',
  int: 'Intelligence',
  wis: 'Wisdom',
  cha: 'Charisma',
}

/** The 18 skills of the 2024 PHB, each keyed to its governing ability. */
export const SKILLS = {
  acrobatics: { name: 'Acrobatics', ability: 'dex' },
  animal_handling: { name: 'Animal Handling', ability: 'wis' },
  arcana: { name: 'Arcana', ability: 'int' },
  athletics: { name: 'Athletics', ability: 'str' },
  deception: { name: 'Deception', ability: 'cha' },
  history: { name: 'History', ability: 'int' },
  insight: { name: 'Insight', ability: 'wis' },
  intimidation: { name: 'Intimidation', ability: 'cha' },
  investigation: { name: 'Investigation', ability: 'int' },
  medicine: { name: 'Medicine', ability: 'wis' },
  nature: { name: 'Nature', ability: 'int' },
  perception: { name: 'Perception', ability: 'wis' },
  performance: { name: 'Performance', ability: 'cha' },
  persuasion: { name: 'Persuasion', ability: 'cha' },
  religion: { name: 'Religion', ability: 'int' },
  sleight_of_hand: { name: 'Sleight of Hand', ability: 'dex' },
  stealth: { name: 'Stealth', ability: 'dex' },
  survival: { name: 'Survival', ability: 'wis' },
} as const satisfies Record<string, { name: string; ability: Ability }>

export type SkillKey = keyof typeof SKILLS
export const SKILL_KEYS = Object.keys(SKILLS) as SkillKey[]

/** 'none' is represented by absence from the map. */
export type ProficiencyLevel = 'proficient' | 'expertise'

export interface ClassDef {
  name: string
  hitDie: 4 | 6 | 8 | 10 | 12
  saves: [Ability, Ability]
  /** Full, half, third, or non-caster — drives the spell slot table. */
  casting: 'full' | 'half' | 'third' | 'pact' | 'none'
  spellcastingAbility: Ability | null
  primary: Ability[]
}

export const CLASSES = {
  barbarian: { name: 'Barbarian', hitDie: 12, saves: ['str', 'con'], casting: 'none', spellcastingAbility: null, primary: ['str'] },
  bard:      { name: 'Bard',      hitDie: 8,  saves: ['dex', 'cha'], casting: 'full', spellcastingAbility: 'cha', primary: ['cha'] },
  cleric:    { name: 'Cleric',    hitDie: 8,  saves: ['wis', 'cha'], casting: 'full', spellcastingAbility: 'wis', primary: ['wis'] },
  druid:     { name: 'Druid',     hitDie: 8,  saves: ['int', 'wis'], casting: 'full', spellcastingAbility: 'wis', primary: ['wis'] },
  fighter:   { name: 'Fighter',   hitDie: 10, saves: ['str', 'con'], casting: 'none', spellcastingAbility: null, primary: ['str', 'dex'] },
  monk:      { name: 'Monk',      hitDie: 8,  saves: ['str', 'dex'], casting: 'none', spellcastingAbility: null, primary: ['dex', 'wis'] },
  paladin:   { name: 'Paladin',   hitDie: 10, saves: ['wis', 'cha'], casting: 'half', spellcastingAbility: 'cha', primary: ['str', 'cha'] },
  ranger:    { name: 'Ranger',    hitDie: 10, saves: ['str', 'dex'], casting: 'half', spellcastingAbility: 'wis', primary: ['dex', 'wis'] },
  rogue:     { name: 'Rogue',     hitDie: 8,  saves: ['dex', 'int'], casting: 'none', spellcastingAbility: null, primary: ['dex'] },
  sorcerer:  { name: 'Sorcerer',  hitDie: 6,  saves: ['con', 'cha'], casting: 'full', spellcastingAbility: 'cha', primary: ['cha'] },
  warlock:   { name: 'Warlock',   hitDie: 8,  saves: ['wis', 'cha'], casting: 'pact', spellcastingAbility: 'cha', primary: ['cha'] },
  wizard:    { name: 'Wizard',    hitDie: 6,  saves: ['int', 'wis'], casting: 'full', spellcastingAbility: 'int', primary: ['int'] },
} as const satisfies Record<string, ClassDef>

export type ClassKey = keyof typeof CLASSES
export const CLASS_KEYS = Object.keys(CLASSES) as ClassKey[]

/** SRD 5.2 species. 2024 rules call these species, not races. */
export const SPECIES = [
  'Aasimar', 'Dragonborn', 'Dwarf', 'Elf', 'Gnome',
  'Goliath', 'Halfling', 'Human', 'Orc', 'Tiefling',
] as const

/** SRD 5.2 backgrounds. Each grants an origin feat and ability increases. */
export const BACKGROUNDS = [
  'Acolyte', 'Artisan', 'Charlatan', 'Criminal', 'Entertainer', 'Farmer',
  'Guard', 'Guide', 'Hermit', 'Merchant', 'Noble', 'Sage', 'Sailor',
  'Scribe', 'Soldier', 'Wayfarer',
] as const

export const ALIGNMENTS = [
  'Lawful Good', 'Neutral Good', 'Chaotic Good',
  'Lawful Neutral', 'Neutral', 'Chaotic Neutral',
  'Lawful Evil', 'Neutral Evil', 'Chaotic Evil',
] as const

/** XP thresholds to reach each level (2024 PHB). Index = level - 1. */
export const XP_THRESHOLDS = [
  0, 300, 900, 2700, 6500, 14000, 23000, 34000, 48000, 64000,
  85000, 100000, 120000, 140000, 165000, 195000, 225000, 265000, 305000, 355000,
] as const

/** Full-caster slots by character level. Index 0 = level 1; inner index 0 = 1st-level slots. */
export const FULL_CASTER_SLOTS: readonly (readonly number[])[] = [
  [2], [3], [4, 2], [4, 3], [4, 3, 2], [4, 3, 3], [4, 3, 3, 1], [4, 3, 3, 2],
  [4, 3, 3, 3, 1], [4, 3, 3, 3, 2], [4, 3, 3, 3, 2, 1], [4, 3, 3, 3, 2, 1],
  [4, 3, 3, 3, 2, 1, 1], [4, 3, 3, 3, 2, 1, 1], [4, 3, 3, 3, 2, 1, 1, 1],
  [4, 3, 3, 3, 2, 1, 1, 1], [4, 3, 3, 3, 2, 1, 1, 1, 1], [4, 3, 3, 3, 3, 1, 1, 1, 1],
  [4, 3, 3, 3, 3, 2, 1, 1, 1], [4, 3, 3, 3, 3, 2, 2, 1, 1],
]

/** Warlock Pact Magic: [slot count, slot level] by character level. */
export const PACT_SLOTS: readonly (readonly [number, number])[] = [
  [1, 1], [2, 1], [2, 2], [2, 2], [2, 3], [2, 3], [2, 4], [2, 4],
  [2, 5], [2, 5], [3, 5], [3, 5], [3, 5], [3, 5], [3, 5], [3, 5],
  [4, 5], [4, 5], [4, 5], [4, 5],
]

export const CONDITIONS = [
  'Blinded', 'Charmed', 'Deafened', 'Frightened', 'Grappled', 'Incapacitated',
  'Invisible', 'Paralyzed', 'Petrified', 'Poisoned', 'Prone', 'Restrained',
  'Stunned', 'Unconscious',
] as const
